import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, ChevronLeft, ChevronRight, Phone, MessageCircle, Loader2, Check, Sparkles, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { trackEvent, getWhatsAppUrl, getPhoneHref } from "@/lib/tracking";
import { submitLead } from "@/lib/leads.functions";
import { buildLeadAttribution } from "@/lib/lead-attribution";

interface SlotProps {
  trackingLocation?: string;
  /** Optional area context, e.g. "poplar" — included on the lead record */
  area?: string;
  className?: string;
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface DaySlots {
  date: Date;
  dayName: string;
  slots: string[]; // "HH:MM" strings
}

/**
 * Generate the next N days of MOT availability based on the admin-managed
 * opening hours and the admin-controlled motAvailability window
 * (windowDays, leadTimeHours, slotMinutes, dailyCapacity, blackoutDates,
 * bookedSlots, overrides). This replaces the previous mock seed so the grid
 * reflects the workshop's real next-available picture.
 */
function generateAvailability(
  openingHours: { days: string[]; opens: string; closes: string }[] | null | undefined,
  availability: {
    windowDays?: number;
    leadTimeHours?: number;
    slotMinutes?: number;
    dailyCapacity?: number;
    blackoutDates?: string[];
    bookedSlots?: { date: string; times?: string[] }[];
    overrides?: { date: string; opens?: string; closes?: string; closed?: boolean }[];
  } | null | undefined,
): DaySlots[] {
  try {
  const days: DaySlots[] = [];
  const safeAvailability = availability || {};
  const safeOpeningHours = Array.isArray(openingHours) && openingHours.length > 0
    ? openingHours
    : [{ days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "09:00", closes: "18:00" }];
  const now = new Date();
  const leadTimeHours = Math.max(0, Math.min(168, Number(safeAvailability.leadTimeHours) || 2));
  const earliest = new Date(now.getTime() + leadTimeHours * 60 * 60 * 1000);

  const blackouts = new Set(Array.isArray(safeAvailability.blackoutDates) ? safeAvailability.blackoutDates : []);
  const bookedByDay = new Map<string, Set<string>>();
  for (const b of Array.isArray(safeAvailability.bookedSlots) ? safeAvailability.bookedSlots : []) bookedByDay.set(b.date, new Set(b.times || []));
  const overridesByDay = new Map<string, { opens?: string; closes?: string; closed?: boolean }>();
  for (const o of Array.isArray(safeAvailability.overrides) ? safeAvailability.overrides : []) overridesByDay.set(o.date, o);

  const range = Math.min(60, Math.max(1, Number(safeAvailability.windowDays) || 14));
  const slotMinutes = Math.max(15, Math.min(120, Number(safeAvailability.slotMinutes) || 60));
  const dailyCapacity = Math.max(1, Math.min(40, Number(safeAvailability.dailyCapacity) || 6));

  for (let i = 0; i < range; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() + i);
    date.setHours(0, 0, 0, 0);
    const isoDate = date.toISOString().slice(0, 10);
    if (blackouts.has(isoDate)) continue;

    const dayName = DAY_NAMES[date.getDay()];
    const rule = safeOpeningHours.find((h) => Array.isArray(h.days) && h.days.includes(dayName));
    const override = overridesByDay.get(isoDate);
    if (override?.closed) continue;
    if (!rule && !override) continue;

    const opens = override?.opens || rule?.opens || "09:00";
    const closes = override?.closes || rule?.closes || "18:00";
    if (!/^\d{2}:\d{2}$/.test(opens) || !/^\d{2}:\d{2}$/.test(closes)) continue;
    const [openH, openM] = opens.split(":").map(Number);
    const [closeH, closeM] = closes.split(":").map(Number);
    const startMin = openH * 60 + (openM || 0);
    const endMin = closeH * 60 + (closeM || 0);
    if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) continue;

    const booked = bookedByDay.get(isoDate) || new Set<string>();
    const slots: string[] = [];
    for (let m = startMin; m + slotMinutes <= endMin; m += slotMinutes) {
      const slotDate = new Date(date);
      slotDate.setHours(Math.floor(m / 60), m % 60, 0, 0);
      if (slotDate < earliest) continue;
      const time = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
      if (booked.has(time)) continue;
      slots.push(time);
      if (slots.length >= dailyCapacity) break;
    }
    days.push({ date, dayName, slots });
  }
  return days;
  } catch (err) {
    if (import.meta.env.DEV) console.warn("MOT availability generation failed", err);
    return [];
  }
}

function formatDateLabel(d: Date): { weekday: string; dayNum: string; month: string } {
  return {
    weekday: d.toLocaleDateString("en-GB", { weekday: "short" }),
    dayNum: d.toLocaleDateString("en-GB", { day: "numeric" }),
    month: d.toLocaleDateString("en-GB", { month: "short" }),
  };
}

export function MOTSlotAvailability({ trackingLocation = "homepage", area, className }: SlotProps) {
  const { compliance, routing } = usePublicConfig();
  const [pageIdx, setPageIdx] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Callback form state (shown when user can't find a slot)
  const [showCallback, setShowCallback] = useState(false);
  const [cbName, setCbName] = useState("");
  const [cbPhone, setCbPhone] = useState("");
  const [cbReg, setCbReg] = useState("");
  const [cbWhen, setCbWhen] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Defer time-based slot generation until after mount so SSR markup matches the
  // first client paint (avoids a hydration mismatch when the server clock differs
  // from the visitor's local clock by a slot-window boundary).
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const allDays = useMemo(
    () => mounted ? generateAvailability(compliance.openingHours, compliance.motAvailability) : [],
    [mounted, compliance.openingHours, compliance.motAvailability],
  );
  const PER_PAGE = 4;
  const totalPages = Math.max(1, Math.ceil(allDays.length / PER_PAGE));
  const visibleDays = allDays.slice(pageIdx * PER_PAGE, pageIdx * PER_PAGE + PER_PAGE);

  // Next available across the whole window
  const nextAvailable = useMemo(() => {
    for (const d of allDays) {
      if (d.slots.length > 0) return { date: d.date, slot: d.slots[0] };
    }
    return null;
  }, [allDays]);

  // Fire "no slot matched" once per mount when the window contains zero open
  // slots so the admin can attribute callbacks to genuine availability gaps.
  const noSlotFired = useRef(false);
  useEffect(() => {
    if (!mounted) return;
    if (!nextAvailable && !noSlotFired.current && allDays.length >= 0) {
      noSlotFired.current = true;
      trackEvent("mot_no_slot_matched", {
        location: trackingLocation,
        window_days: compliance.motAvailability.windowDays,
        ...(area ? { area } : {}),
      });
    }
  }, [mounted, nextAvailable, allDays.length, trackingLocation, area, compliance.motAvailability.windowDays]);

  function handleSlotPick(date: Date, slot: string) {
    setSelectedDate(date);
    setSelectedSlot(slot);
    setShowCallback(false);
    trackEvent("mot_slot_selected", {
      location: trackingLocation,
      slot_date: date.toISOString().slice(0, 10),
      slot_time: slot,
    });
  }

  function buildPrefilledWhatsApp(): string {
    if (!selectedDate || !selectedSlot) return getWhatsAppUrl("mot");
    const dateStr = selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
    const number = (routing.whatsappNumber || "442075372447").replace(/[^\d]/g, "");
    const msg = [
      `Hi Woodseer Autos — I'd like to book an MOT for ${dateStr} at ${selectedSlot}.`,
      area ? `I'm in ${area}.` : "",
      "Please confirm if this slot is available — thanks.",
    ].filter(Boolean).join("\n");
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  }

  async function handleCallbackSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (cbName.trim().length < 2 || cbPhone.trim().length < 7) return;
    setSubmitting(true);
    setError("");
    const preferredContact = compliance.motAvailability.callbackPreference || "phone";
    trackEvent("mot_callback_request", {
      location: trackingLocation,
      has_when: !!cbWhen,
      preferred_contact: preferredContact,
    });
    try {
      const attribution = buildLeadAttribution({
        formLocation: `mot_slot_${trackingLocation}`,
        ctaLabel: "mot_callback_request",
        pageType: area ? "area" : "service",
      });
      await submitLead({
        data: {
          type: "callback" as const,
          service: "mot",
          name: cbName.trim(),
          phone: cbPhone.trim(),
          vehicle_reg: cbReg.trim() || undefined,
          callback_time: cbWhen.trim() || undefined,
          notes: `MOT callback — no slot matched on availability widget.${area ? ` Area: ${area}.` : ""}`,
          area: area || undefined,
          preferred_contact: preferredContact,
          ...attribution,
        },
      });
      trackEvent("mot_callback_sent", {
        location: trackingLocation,
        has_when: !!cbWhen,
        has_reg: !!cbReg.trim(),
        no_slot_match: !nextAvailable,
        preferred_contact: preferredContact,
        ...(area ? { area } : {}),
      });
      setSuccess(true);
    } catch {
      setError("Couldn't send your request. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleConfirmSelected() {
    if (!selectedDate || !selectedSlot) return;
    trackEvent("mot_slot_confirm_request", {
      location: trackingLocation,
      slot_date: selectedDate.toISOString().slice(0, 10),
      slot_time: selectedSlot,
    });
    // Pre-fill name/phone shortcut: open callback form with the slot recorded as preferred
    setCbWhen(`${selectedDate.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} ${selectedSlot}`);
    setShowCallback(true);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`rounded-3xl border border-border bg-card shadow-xl shadow-black/[0.04] overflow-hidden ${className || ""}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border px-5 md:px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
            <Calendar className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">Live MOT availability</p>
            <h3 className="mt-0.5 text-base md:text-lg font-bold text-foreground tracking-tight leading-tight">
              Next available MOT slots
            </h3>
          </div>
          {nextAvailable && (
            <div className="hidden sm:block text-right shrink-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Soonest</p>
              <p className="text-xs font-bold text-foreground">
                {nextAvailable.date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {nextAvailable.slot}
              </p>
            </div>
          )}
          <Link
            to="/book"
            search={{ service: "mot", ...(area ? { area } : {}) }}
            onClick={() =>
              trackEvent("service_cta_click", {
                location: `${trackingLocation}_widget_book_mot`,
                source: "mot_slot_widget",
                service: "mot",
                ...(area ? { area } : {}),
              })
            }
            className="hidden md:inline-flex items-center gap-1 rounded-lg bg-primary/90 px-3 py-1.5 text-[11px] font-bold text-primary-foreground hover:bg-primary transition-colors shrink-0"
          >
            Book MOT <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Day columns + slots */}
      <div className="px-5 md:px-6 pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {pageIdx === 0 ? "This week" : "Next week"}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
              disabled={pageIdx === 0}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:text-foreground disabled:opacity-40"
              aria-label="Previous days"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setPageIdx((i) => Math.min(totalPages - 1, i + 1))}
              disabled={pageIdx >= totalPages - 1}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:text-foreground disabled:opacity-40"
              aria-label="Next days"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {visibleDays.length === 0 || !nextAvailable ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-5 text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="mt-3 text-base font-bold text-foreground">Slots move quickly — let's check today's availability</p>
            <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
              The visible window for the next {compliance.motAvailability.windowDays} days looks tight, but workshop diary changes daily.
              {compliance.motAvailability.callbackPreference === "whatsapp"
                ? " Send a quick WhatsApp or request a callback and we'll find you a time."
                : " Call or request a callback and we'll find you a time."}
            </p>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setShowCallback(true);
                  trackEvent("mot_callback_open", {
                    location: trackingLocation,
                    source: "no_slots_empty_state",
                    ...(area ? { area } : {}),
                  });
                }}
                className="font-bold gap-1.5"
              >
                <Phone className="h-3.5 w-3.5" />
                {compliance.motAvailability.callbackPreference === "whatsapp" ? "Request WhatsApp callback" : "Request a callback"}
              </Button>
              <Link
                to="/book"
                search={{ service: "mot", ...(area ? { area } : {}) }}
                onClick={() =>
                  trackEvent("service_cta_click", {
                    location: `${trackingLocation}_no_slots_book`,
                    source: "mot_slot_widget_empty",
                    service: "mot",
                    ...(area ? { area } : {}),
                  })
                }
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
              >
                Open booking form <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {visibleDays.map((d) => {
              const lbl = formatDateLabel(d.date);
              return (
                <div key={d.date.toISOString()} className="rounded-xl border border-border bg-background overflow-hidden">
                  <div className="bg-muted/40 px-2 py-1.5 text-center border-b border-border">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{lbl.weekday}</p>
                    <p className="text-sm font-bold text-foreground leading-tight">{lbl.dayNum} {lbl.month}</p>
                  </div>
                  <div className="p-1.5 space-y-1 min-h-[110px]">
                    {d.slots.length === 0 ? (
                      <p className="text-[10px] text-muted-foreground text-center py-3">Fully booked</p>
                    ) : (
                      d.slots.slice(0, 4).map((slot) => {
                        const active = selectedDate?.toDateString() === d.date.toDateString() && selectedSlot === slot;
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => handleSlotPick(d.date, slot)}
                            className={`w-full rounded-md px-2 py-1.5 text-[11px] font-bold transition-all ${
                              active
                                ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                                : "bg-muted/40 text-foreground hover:bg-primary/10 hover:text-primary"
                            }`}
                          >
                            <Clock className="h-2.5 w-2.5 inline mr-1 -mt-0.5" />
                            {slot}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-[10px] text-muted-foreground italic flex items-start gap-1.5">
          <Sparkles className="h-3 w-3 text-primary shrink-0 mt-0.5" />
          Live availability based on workshop diary — pick a slot and we'll confirm by phone or WhatsApp.
        </p>
      </div>

      {/* Selected slot CTA strip */}
      <AnimatePresence initial={false}>
        {selectedDate && selectedSlot && !showCallback && !success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-border bg-primary/[0.04]"
          >
            <div className="px-5 md:px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-foreground">
                  <span className="font-bold">Selected:</span>{" "}
                  {selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} at{" "}
                  <span className="font-bold text-primary">{selectedSlot}</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <Button onClick={handleConfirmSelected} size="sm" className="font-bold">
                    Request this slot
                  </Button>
                  <a
                    href={buildPrefilledWhatsApp()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("whatsapp_click", { location: `${trackingLocation}_slot_pick`, service: "mot" })}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#25D366]/40 bg-[#25D366]/10 px-3 py-1.5 text-xs font-bold text-[#1ea952] hover:bg-[#25D366]/15 transition-colors"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer: callback request CTA */}
      {!showCallback && !success && (
        <div className="border-t border-border bg-muted/30 px-5 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Nothing matches?</span> Request a callback and we'll find a time that works.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCallback(true);
                  trackEvent("mot_callback_open", { location: trackingLocation });
                }}
                className="font-bold"
              >
                Request a callback
              </Button>
              <a
                href={getPhoneHref("mot")}
                onClick={() => trackEvent("call_click", { location: `${trackingLocation}_slot_grid` })}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-bold text-background hover:bg-foreground/90 transition-colors"
              >
                <Phone className="h-3.5 w-3.5" /> Call
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Callback form */}
      <AnimatePresence initial={false}>
        {showCallback && !success && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleCallbackSubmit}
            className="border-t border-border bg-muted/20 overflow-hidden"
          >
            <div className="px-5 md:px-6 py-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-foreground">Request a callback</p>
                <button
                  type="button"
                  onClick={() => setShowCallback(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cb-name" className="text-[11px] font-bold uppercase tracking-wider">Name *</Label>
                  <Input id="cb-name" value={cbName} onChange={(e) => setCbName(e.target.value)} required minLength={2} maxLength={100} className="mt-1 h-10" placeholder="Full name" />
                </div>
                <div>
                  <Label htmlFor="cb-phone" className="text-[11px] font-bold uppercase tracking-wider">Phone *</Label>
                  <Input id="cb-phone" type="tel" value={cbPhone} onChange={(e) => setCbPhone(e.target.value)} required minLength={7} maxLength={20} className="mt-1 h-10" placeholder="07…" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cb-reg" className="text-[11px] font-bold uppercase tracking-wider">Reg <span className="text-muted-foreground normal-case font-normal">(optional)</span></Label>
                  <Input id="cb-reg" value={cbReg} onChange={(e) => setCbReg(e.target.value.toUpperCase())} maxLength={10} className="mt-1 h-10 uppercase tracking-widest" placeholder="AB12 CDE" />
                </div>
                <div>
                  <Label htmlFor="cb-when" className="text-[11px] font-bold uppercase tracking-wider">When works? <span className="text-muted-foreground normal-case font-normal">(optional)</span></Label>
                  <Input id="cb-when" value={cbWhen} onChange={(e) => setCbWhen(e.target.value)} maxLength={120} className="mt-1 h-10" placeholder="e.g. Tue afternoon" />
                </div>
              </div>

              {error && <p className="text-xs text-destructive font-medium">{error}</p>}

              <Button type="submit" disabled={submitting} className="w-full h-11 font-bold">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Sending…</>
                ) : (
                  <>Request callback</>
                )}
              </Button>
              <p className="text-[10px] text-center text-muted-foreground">
                We'll only use your details to confirm your booking.
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Success state */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border-t border-primary/30 bg-primary/[0.05] px-5 md:px-6 py-5 text-center"
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Check className="h-5 w-5" />
            </div>
            <p className="mt-2.5 text-sm font-bold text-foreground">Callback request received</p>
            <p className="mt-1 text-xs text-muted-foreground">We'll call you back to confirm a time. Usually within a few hours during workshop hours.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
